/**
 * SplitReviewModal — check how an auto-separated file will be split, before saving.
 *
 * Shows every page of the picked PDF as a thumbnail, boxed into the documents the
 * reader suggested (utils/pageGrouping.ts). Between each pair of pages sits one
 * button: "Split" inside a document, "Join" between two documents. Boundaries the
 * reader was unsure of are marked "Check". Nothing is saved until "Save".
 *
 * Keyboard: Tab walks pages and boundary buttons in page order; Enter / Space toggles one;
 * Esc closes the enlarged page, then the dialog (the file keeps waiting).
 *
 * Also used by Process Docs → "Separate pages" on a stored document (`edit` prop): no
 * suggested split there — every page starts joined — and saving REPLACES the original,
 * so the footer carries the "cannot be undone" warning.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { UploadJob } from '../utils/uploadQueue';
import { groupsFromCuts, SplitPlan } from '../utils/pageGrouping';
import './UploadDocsPage.css';   // the ud-split-* styles (also needed when opened from Process Docs)

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const THUMB_WIDTH = 150;
const ZOOM_WIDTH = 900;

/** A document already stored, opened from Process Docs to separate its pages. */
export interface SplitEditSource {
  name: string;
  bytes: Uint8Array;
  pageCount: number;
  busy?: boolean;
}

interface SplitReviewModalProps {
  /** Upload being checked (auto-separate) … */
  job?: UploadJob;
  /** … or a stored document whose pages are being separated. */
  edit?: SplitEditSource;
  /** Save the file as these documents (0-based page indexes per document). */
  onConfirm: (groups: number[][]) => void;
  /** Close without deciding — the file stays in the list, waiting. */
  onClose: () => void;
}

async function renderPage(pdf: any, pageNo: number, width: number): Promise<string> {
  const page = await pdf.getPage(pageNo);
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: width / base.width });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  await page.render({ canvasContext: ctx, viewport, intent: 'print' } as any).promise;
  const url = canvas.toDataURL('image/jpeg', 0.8);
  // Free the page image and the canvas now — a long scan is hundreds of these.
  canvas.width = 0;
  canvas.height = 0;
  page.cleanup();
  return url;
}

const SplitReviewModal: React.FC<SplitReviewModalProps> = ({ job, edit, onConfirm, onClose }) => {
  // Separating a stored document starts with every page in one document, nothing to check.
  const plan: SplitPlan = useMemo(() => {
    if (job?.split) return job.split;
    const n = Math.max(1, edit?.pageCount || 1);
    return {
      pageCount: n,
      boundaries: Array.from({ length: n - 1 }, () => ({ startsNew: false, confident: true, reason: '' })),
      groups: [Array.from({ length: n }, (_, i) => i)],
    };
  }, [job, edit?.pageCount]);
  const name = job ? job.name : edit?.name || '';
  const pageCount = plan.pageCount;
  // cuts[i] = a new document starts after page i (0-based).
  const [cuts, setCuts] = useState<boolean[]>(() => plan.boundaries.map(b => b.startsNew));
  // Boundaries the user has set by hand no longer need the "Check" mark.
  const [touched, setTouched] = useState<Set<number>>(() => new Set());
  const [thumbs, setThumbs] = useState<string[]>([]);
  const [zoom, setZoom] = useState<{ page: number; url: string } | null>(null);
  const pdfRef = useRef<any>(null);
  const firstBoundaryRef = useRef<HTMLButtonElement | null>(null);

  // Thumbnails, one page at a time so the first ones show straight away.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bytes = job ? new Uint8Array(await job.file.arrayBuffer()) : edit!.bytes.slice();
        const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        for (let i = 1; i <= pdf.numPages; i++) {
          const url = await renderPage(pdf, i, THUMB_WIDTH * 2);
          if (cancelled) return;
          setThumbs(prev => { const next = prev.slice(); next[i - 1] = url; return next; });
        }
      } catch (err) {
        console.warn('[SplitReviewModal] Could not draw the pages:', err);
      }
    })();
    return () => {
      cancelled = true;
      // Close the document, or its worker keeps the whole file after the check is done.
      const pdf = pdfRef.current;
      pdfRef.current = null;
      try { pdf?.destroy(); } catch { /* already closed */ }
    };
  }, [job?.file, edit?.bytes]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { firstBoundaryRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (zoom) setZoom(null); else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom, onClose]);

  const groups = useMemo(() => groupsFromCuts(pageCount, cuts), [pageCount, cuts]);
  const multiPage = groups.filter(g => g.length > 1).length;
  const unsure = plan.boundaries.filter((b, i) => !b.confident && !touched.has(i)).length;

  const toggle = useCallback((i: number) => {
    setCuts(prev => prev.map((c, idx) => (idx === i ? !c : c)));
    setTouched(prev => new Set(prev).add(i));
  }, []);

  const setAll = useCallback((value: boolean) => {
    setCuts(Array(pageCount - 1).fill(value));
    setTouched(new Set(Array.from({ length: pageCount - 1 }, (_, i) => i)));
  }, [pageCount]);

  const openZoom = useCallback(async (pageIdx: number) => {
    if (!pdfRef.current) return;
    const url = await renderPage(pdfRef.current, pageIdx + 1, ZOOM_WIDTH);
    if (url) setZoom({ page: pageIdx + 1, url });
  }, []);

  // The button between page i and page i+1.
  const boundaryButton = (i: number) => {
    const isCut = cuts[i];
    const check = !plan.boundaries[i].confident && !touched.has(i);
    return (
      <button
        key={`b${i}`}
        ref={i === 0 ? firstBoundaryRef : undefined}
        type="button"
        className={`ud-split-boundary ${isCut ? 'cut' : 'joined'} ${check ? 'check' : ''}`}
        onClick={() => toggle(i)}
        title={`${plan.boundaries[i].reason ? `${plan.boundaries[i].reason}\n` : ''}${isCut
          ? `Click to join page ${i + 2} to the document before it.`
          : `Click to start a new document at page ${i + 2}.`}`}
        aria-label={isCut
          ? `Pages ${i + 1} and ${i + 2} are separate documents. Join them.`
          : `Pages ${i + 1} and ${i + 2} are one document. Split them.`}
      >
        <span className="ud-split-boundary-icon" aria-hidden="true">{isCut ? '⇆' : '✂'}</span>
        <span>{isCut ? 'Join' : 'Split'}</span>
        {check && <span className="ud-split-check">Check</span>}
      </button>
    );
  };

  return (
    <div className="ud-split-overlay" onClick={onClose}>
      <div
        className="ud-split-modal" onClick={e => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label={edit ? `Separate the pages of ${name}` : `Check the split of ${name}`}
      >
        <div className="ud-split-header">
          <div>
            <h3 className="ud-split-title">{edit ? 'Separate pages' : 'Check the split'} — {name}</h3>
            <p className="ud-split-sub">
              Each box is one document. Use <strong>Split</strong> and <strong>Join</strong> between
              pages to {edit ? 'separate them' : 'correct it'}; click a page to enlarge it.
              {unsure > 0 && (
                <span className="ud-split-unsure"> {unsure} {unsure === 1 ? 'place is' : 'places are'} marked “Check”.</span>
              )}
            </p>
          </div>
          <div className="ud-split-quick">
            <button type="button" className="ud-btn ud-btn-secondary" onClick={() => setAll(true)}>Every page separate</button>
            <button type="button" className="ud-btn ud-btn-secondary" onClick={() => setAll(false)}>Keep as one document</button>
          </div>
        </div>

        <div className="ud-split-body">
          {groups.map((group, gi) => (
            <React.Fragment key={group[0]}>
              <div className={`ud-split-group ${group.length > 1 ? 'multi' : ''}`}>
                <div className="ud-split-group-label">
                  Document {gi + 1}
                  <span className="ud-split-group-pages">
                    {group.length === 1
                      ? ` · page ${group[0] + 1}`
                      : ` · pages ${group[0] + 1}–${group[group.length - 1] + 1} (${group.length} pages)`}
                  </span>
                </div>
                <div className="ud-split-pages">
                  {group.map((pageIdx, pi) => (
                    <React.Fragment key={pageIdx}>
                      <button
                        type="button" className="ud-split-page"
                        onClick={() => openZoom(pageIdx)} title={`Page ${pageIdx + 1} — click to enlarge`}
                      >
                        {thumbs[pageIdx]
                          ? <img src={thumbs[pageIdx]} alt={`Page ${pageIdx + 1}`} width={THUMB_WIDTH} />
                          : <span className="ud-split-page-wait" style={{ width: THUMB_WIDTH }}>…</span>}
                        <span className="ud-split-page-no">{pageIdx + 1}</span>
                      </button>
                      {pi < group.length - 1 && boundaryButton(pageIdx)}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              {gi < groups.length - 1 && boundaryButton(group[group.length - 1])}
            </React.Fragment>
          ))}
        </div>

        <div className="ud-split-footer">
          <span className="ud-split-summary">
            {pageCount} pages → <strong>{groups.length} {groups.length === 1 ? 'document' : 'documents'}</strong>
            {' '}({groups.length - multiPage} single-page, {multiPage} multi-page)
          </span>
          {edit && groups.length > 1 && (
            <span className="ud-split-warning" role="alert">
              ⚠ The original document will be replaced by these {groups.length} documents. This cannot be undone.
            </span>
          )}
          <div className="ud-split-actions">
            <button type="button" className="ud-btn ud-btn-secondary" onClick={onClose} disabled={edit?.busy}>
              {edit ? 'Cancel' : 'Not now'}
            </button>
            {edit ? (
              <button type="button" className="ud-btn ud-btn-primary ud-btn-danger"
                disabled={groups.length < 2 || edit.busy}
                title={groups.length < 2 ? 'Use Split between pages to make more than one document' : undefined}
                onClick={() => onConfirm(groups)}>
                {edit.busy ? 'Saving…' : groups.length < 2 ? 'Split pages first' : `Replace with ${groups.length} documents`}
              </button>
            ) : (
              <button type="button" className="ud-btn ud-btn-primary" onClick={() => onConfirm(groups)}>
                Save {groups.length} {groups.length === 1 ? 'document' : 'documents'}
              </button>
            )}
          </div>
        </div>

        {zoom && (
          <div className="ud-split-zoom" onClick={() => setZoom(null)} title="Click to close">
            <img src={zoom.url} alt={`Page ${zoom.page}`} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SplitReviewModal;
